// src/components/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Search,
  Database,
  Users,
  Award,
  DollarSign,
  Lock,
  ChevronLeft,
  ChevronRight,
  Check,
  MapPin,
  BarChart3,
  Shield,
  TrendingUp,
  Minus,
  Menu,
  X
} from 'lucide-react';
import axios from 'axios';
import './LandingPage.css';
import TickerBar from './TickerBar';
import ScrollToTopLink from './ScrollRestoration';

// Assets
import medinahGif from '../Assets/Images/pinevalley.GIF';
import mapGif from '../Assets/Images/floridamap.GIF';
import appScreenshot from '../Assets/Images/app-screenshot.png';
import darkLogo from '../Assets/Images/Dark_Logo_Transparent.png';

// JSON-LD Structured Data
const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  logo: 'https://privateiq.golf/logo192.png',
  sameAs: []
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://privateiq.golf/search?q={query}',
    'query-input': 'required name=query'
  }
};

// Testimonials data
const testimonials = [
  { text: "PrivateIQ feels like the secret club for private clubs—I'm almost annoyed I didn't think of it first.", name: "Sarah W." },
  { text: "Finally, I don't have to play detective just to figure out initiation fees—thank you, PrivateIQ!", name: "Mike R." },
  { text: "Who knew comparing clubs and chatting with similar golfers could be this addictive?", name: "Emily K." },
  { text: "This site is like Yelp for golf clubs, but without the nonsense reviews from people who don't actually play golf.", name: "Jake P." },
  { text: "It's like PrivateIQ crawled into my brain and figured out exactly what I needed as a golfer—spooky, but I'll take it.", name: "Anna L." },
  { text: "I'm not sure what I love more: the ridiculously detailed data or the fact that I can actually talk to others about it.", name: "Chris M." },
  { text: "It's rare to find a platform this sleek that also has real substance—usually you only get one or the other.", name: "Laura T." },
  { text: "The forums are surprisingly drama-free, which, for golfers, is saying something.", name: "Tom B." },
  { text: "I came for the data, stayed for the community, and now I'm basically running my own clubhouse here.", name: "Jenny L." },
  { text: "PrivateIQ makes the private club world feel a little less…private.", name: "Sam D." },
  { text: "It's like having an insider golf concierge, but without the awkward small talk.", name: "Brian F." },
  { text: "I didn't know I needed this until I joined—and now I can't shut up about it.", name: "Kelly P." },
  { text: "PrivateIQ solved a problem I didn't realize I had—turns out, I've been blindly guessing about clubs my whole life.", name: "Andrew H." },
  { text: "This platform is a cheat code for navigating the private golf world—don't tell too many people, though.", name: "Maggie S." },
  { text: "Who knew comparing clubs and avoiding bad decisions could actually be fun?", name: "Chris J." },
  { text: "It's basically a clubhouse for my phone—except this one actually has good Wi-Fi.", name: "Alex C." },
  { text: "Finally, a place where I can get real answers about clubs without digging through 20-year-old forum threads.", name: "Sophia G." },
  { text: "I've made better connections here in a week than I have in years at my actual club.", name: "Nathan O." },
  { text: "PrivateIQ gives me the information on clubs I never thought I'd have access to—it's like having a backstage pass to the golf world.", name: "Zoe P." },
];

// Animated Headline Component
const AnimatedHeadline = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const words = ['Data', 'Clubs'];

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <h1 className="hero-headline">
      All the{' '}
      <span className="headline-word-container">
        <span key={wordIndex} className="headline-word animate-flip">
          {words[wordIndex]}
        </span>
      </span>
      <br />
      All in One Place
    </h1>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
  <div className="feature-card-new">
    <div className="feature-icon-box">
      {icon}
    </div>
    <h3 className="feature-card-title">{title}</h3>
    <p className="feature-card-description">{description}</p>
  </div>
);

// Process Step Component
const ProcessStep = ({ number, icon, title, description, note }) => (
  <div className="process-step">
    <div className="process-number">{number}</div>
    <div className="process-content">
      <div className="process-header">
        <div className="process-icon-box">{icon}</div>
        <h3 className="process-title">{title}</h3>
      </div>
      <p className="process-description">{description}</p>
      <div className="process-note">
        <p>{note}</p>
      </div>
    </div>
  </div>
);

// Membership Feature Component
const MembershipFeature = ({ text }) => (
  <div className="membership-feature">
    <Check className="membership-check" strokeWidth={3} />
    <span>{text}</span>
  </div>
);

const LandingPage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const location = useLocation();

  // Header state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Promo code modal state
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoData, setPromoData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Handle scroll for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonial navigation
  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Check for influencer referral code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refSlug = params.get('ref');

    if (refSlug) {
      axios.get(`https://lionfish-app-w73zu.ondigitalocean.app/api/promo-code-by-ref/${refSlug}/`)
        .then(response => {
          const data = response.data;
          setPromoData(data);
          setShowPromoModal(true);
          localStorage.setItem('influencer_promo_code', data.code);
          localStorage.setItem('influencer_name', data.influencer_name || '');
        })
        .catch(error => {
          console.error('Error fetching promo code:', error);
        });
    }
  }, [location]);

  // Copy promo code
  const copyPromoCode = () => {
    if (promoData?.code) {
      navigator.clipboard.writeText(promoData.code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  };

  const closeModal = () => setShowPromoModal(false);

  // Preload GIFs
  useEffect(() => {
    [medinahGif, mapGif].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Smooth scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // SEO strings
  const seoTitle = 'PrivateIQ – Private Golf Club Data & Insights';
  const seoDescription = 'PrivateIQ is the source for private golf club data: initiation fees, dues, amenities, scorecards, and community insights across 2,500+ clubs nationwide.';

  // Handle nav click (closes mobile menu and scrolls)
  const handleNavClick = (sectionId) => {
    setMobileMenuOpen(false);
    scrollToSection(sectionId);
  };

  return (
    <div className="landing-page-new">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://privateiq.golf/" />
        <meta name="application-name" content="PrivateIQ" />
        <meta name="theme-color" content="#2c5c4f" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PrivateIQ" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://privateiq.golf/" />
        <meta property="og:image" content="https://privateiq.golf/og-cover.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://privateiq.golf/og-cover.jpg" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      </Helmet>

      {/* Landing Page Header */}
      <header className={`landing-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="landing-header-container">
          {/* Logo */}
          <Link to="/" className="landing-header-logo">
            <img src={darkLogo} alt="PrivateIQ" className="header-logo-img" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="landing-header-nav">
            <button onClick={() => handleNavClick('features')}>Features</button>
            <button onClick={() => handleNavClick('demos')}>Platform</button>
            <button onClick={() => handleNavClick('how-it-works')}>Process</button>
            <button onClick={() => handleNavClick('testimonials')}>Members</button>
            <Link to="/fellowship" className="landing-header-nav-link">Fellowship</Link>
          </nav>

          {/* Auth Buttons (Desktop) */}
          <div className="landing-header-auth">
            <div className="landing-header-divider"></div>
            <Link to="/login" className="landing-header-login">Login</Link>
            <Link to="/register" className="landing-header-register">Get Started</Link>
          </div>

          {/* Mobile Auth + Menu Button */}
          <div className="landing-header-mobile-auth">

            <Link to="/register" className="mobile-register"><strong>Create Account</strong></Link>
            <button
              className="landing-header-mobile-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="landing-mobile-menu">
            <nav className="landing-mobile-nav">
              <button onClick={() => handleNavClick('features')}>Features</button>
              <button onClick={() => handleNavClick('demos')}>Platform</button>
              <button onClick={() => handleNavClick('how-it-works')}>Process</button>
              <button onClick={() => handleNavClick('testimonials')}>Members</button>
              <Link to="/fellowship" onClick={() => setMobileMenuOpen(false)}>Fellowship</Link>
            </nav>
            <div className="landing-mobile-auth">
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/register" className="mobile-register-btn" onClick={() => setMobileMenuOpen(false)}>
                Register
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Ticker Bar */}
      <div className="ticker-wrapper">
        <TickerBar />
      </div>

      {/* Hero Section */}
      <section className="hero-section-new" aria-label="Hero">
        {/* Brand-forward H1 helps you win the 'privateiq' query */}
        <h1 className="visually-hidden">
          PrivateIQ — Private Golf Club Data &amp; Insights
        </h1>
        <div className="hero-background-pattern"></div>
        <div className="hero-container">
          <div className="hero-grid">
            {/* Headline */}
            <AnimatedHeadline />

            {/* Mobile-only note above phone */}
            <p className="app-store-note">Must register online to access iOS app</p>

            {/* Phone Image */}
            <div className="hero-phone-image">
              <img
                src={appScreenshot}
                alt="PrivateIQ mobile app interface showing club map"
              />
            </div>

            {/* CTA Section */}
            <div className="hero-cta-section">
              <a
                href="https://apps.apple.com/us/app/privateiq/id6756243587"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store-link"
              >
                <img
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1234567890"
                  alt="Download on the App Store"
                />
              </a>
              <p className="hero-cta-note">
                Companion app for registered members.{' '}
                <Link to="/register" className="inline-link">
                  Create account online
                </Link>{' '}
                to access.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">2,500+</div>
              <div className="stat-label">Private Clubs</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">$1,000+</div>
              <div className="stat-label">Quarterly Rewards</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section-new">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-new">Comprehensive Club Intelligence</h2>
            <p className="section-subtitle">Every detail you need to make informed membership decisions.</p>
          </div>

          <div className="features-grid-new">
            <FeatureCard
              icon={<Search className="w-5 h-5" />}
              title="Club Discovery"
              description="Discover private clubs near you and across the country with advanced filtering and search capabilities."
            />
            <FeatureCard
              icon={<Database className="w-5 h-5" />}
              title="Membership Data"
              description="Detailed insights into initiation fees, monthly dues, club financials, and much more."
            />
            <FeatureCard
              icon={<Users className="w-5 h-5" />}
              title="Member Network"
              description="Connect with like-minded members while maintaining complete anonymity if preferred."
            />
            <FeatureCard
              icon={<Award className="w-5 h-5" />}
              title="Club Partnerships"
              description="Optional information exchange with select growing clubs seeking quality members."
            />
            <FeatureCard
              icon={<DollarSign className="w-5 h-5" />}
              title="Contributor Rewards"
              description="Quarterly distribution of $1,000+ among members who submit verified updates."
            />
            <FeatureCard
              icon={<Lock className="w-5 h-5" />}
              title="Privacy First"
              description="Your personal information is never shared without explicit written consent."
            />
          </div>
        </div>
      </section>

      {/* Platform Section */}
      <section id="demos" className="platform-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-new">Platform Overview</h2>
            <p className="section-subtitle">Sophisticated tools designed for discerning members.</p>
          </div>

          <div className="platform-grid">
            <div className="platform-card">
              <div className="platform-card-content">
                <div className="platform-card-header">
                  <div className="platform-icon-box">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <h3 className="platform-card-title">Detailed Analytics</h3>
                </div>
                <p className="platform-card-description">
                  Comprehensive club profiles including cost estimates, course details, amenities,
                  historical data, and member-submitted insights.
                </p>
              </div>
              <div className="platform-card-image">
                <img src={medinahGif} alt="Detailed club analytics dashboard" />
              </div>
            </div>

            <div className="platform-card">
              <div className="platform-card-content">
                <div className="platform-card-header">
                  <div className="platform-icon-box alt">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <h3 className="platform-card-title">Geographic Intelligence</h3>
                </div>
                <p className="platform-card-description">
                  Interactive mapping allows you to find answers that would take hours <strong>in seconds</strong> with advanced filters and search across all regions.
                </p>
              </div>
              <div className="platform-card-image">
                <img src={mapGif} alt="Interactive geographic map interface" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-new">Membership Process</h2>
            <p className="section-subtitle">A transparent process that prioritizes your privacy and control.</p>
          </div>

          <div className="process-steps">
            <ProcessStep
              number="01"
              icon={<Users className="w-6 h-6" />}
              title="Account Creation"
              description="Register with key demographic details including household income, marital status, and family composition."
              note="Your contact information—name, email, phone number—remains strictly confidential and is never shared without your explicit approval."
            />
            <ProcessStep
              number="02"
              icon={<Shield className="w-6 h-6" />}
              title="Anonymous Profiles"
              description="We share anonymized demographic profiles with clubs to help them understand potential membership interest in their area."
              note="These profiles contain no personally identifiable information and ensure complete privacy."
            />
            <ProcessStep
              number="03"
              icon={<TrendingUp className="w-6 h-6" />}
              title="Controlled Engagement"
              description="If a club expresses interest in your anonymous profile, we notify you and await your decision."
              note="Upon your approval to share contact information and subsequent invitation, clubs are required to provide one complimentary round."
            />
          </div>

          {/* Transparency Statement */}
          <div className="transparency-box">
            <div className="transparency-accent"></div>
            <div className="transparency-content">
              <h3 className="transparency-title">Transparency & Development</h3>
              <p className="transparency-quote">
                "Pioneering innovative opportunities for clubs to interact with potential members is fundamental to our long-term strategy."
              </p>
              <div className="transparency-divider"></div>
              <p className="transparency-text">
                While we continue building our membership base to attract club partnerships, this feature will evolve as we onboard participating clubs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section-new">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-new">Member Testimonials</h2>
            <p className="section-subtitle">Reflections from our community.</p>
          </div>

          <div className="testimonial-wrapper">
            <div className="testimonial-card-new" key={currentTestimonial}>
              <div className="testimonial-accent">
                <Minus className="w-12 h-1" strokeWidth={3} />
              </div>
              <p className="testimonial-text-new">
                "{testimonials[currentTestimonial].text}"
              </p>
              <p className="testimonial-name-new">
                {testimonials[currentTestimonial].name}
              </p>
            </div>

            <button
              onClick={prevTestimonial}
              className="testimonial-nav prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextTestimonial}
              className="testimonial-nav next"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="testimonial-indicators">
              {testimonials.slice(0, 5).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`indicator ${idx === currentTestimonial % 5 ? 'active' : ''}`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section-new">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-new">Membership</h2>
            <p className="section-subtitle">Straightforward pricing with complete access.</p>
          </div>

          <div className="pricing-card-new">
            <div className="pricing-card-inner">
              <h3 className="pricing-plan-title">Annual Membership</h3>
              <div className="pricing-amount">
                <span className="price">$20</span>
                <span className="period">/year</span>
              </div>
              <p className="pricing-note">Billed annually - Full platform access</p>

              <div className="pricing-features-list">
                <MembershipFeature text="Complete access to 2,500+ club profiles" />
                <MembershipFeature text="Detailed membership cost analysis" />
                <MembershipFeature text="Interactive geographic mapping" />
                <MembershipFeature text="Member forum participation" />
                <MembershipFeature text="Quarterly contributor rewards program" />
                <MembershipFeature text="Ad-free experience" />
                <MembershipFeature text="Cancel anytime" />
              </div>

              <ScrollToTopLink to="/register" className="pricing-cta-btn">
                Subscribe Now
              </ScrollToTopLink>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">Begin Your Membership</h2>
          <p className="final-cta-subtitle">
            Join discerning golfers accessing the most comprehensive private club database available.
          </p>
          <ScrollToTopLink to="/register" className="final-cta-btn">
            Subscribe for $20/Year
          </ScrollToTopLink>
          <p className="final-cta-note">Complete access - Cancel anytime</p>
        </div>
      </section>

      {/* Promo Modal */}
      {showPromoModal && promoData && (
        <div className="promo-modal-overlay" onClick={closeModal}>
          <div className="promo-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="promo-modal-close" onClick={closeModal} aria-label="Close modal">
              x
            </button>
            <div className="promo-modal-body">
              <h2 className="promo-modal-title">
                {promoData.influencer_name && `${promoData.influencer_name} sent you here!`}
              </h2>
              <p className="promo-modal-subtitle">
                Get {promoData.discount_percent}% off your PrivateIQ membership
              </p>
              <div className="promo-code-display">
                <code className="promo-code-text">{promoData.code}</code>
              </div>
              <button
                className="promo-copy-btn"
                onClick={copyPromoCode}
                disabled={copied}
              >
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
              {promoData.description && (
                <p className="promo-modal-description">{promoData.description}</p>
              )}
              <p className="promo-modal-info">
                Apply code at checkout to receive discount.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer-new">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">privateIQ</div>
              <p className="footer-tagline">The modern source for private golf club intelligence.</p>
            </div>
            <div className="footer-links">
              <h4>Platform</h4>
              <ul>
                <li><button onClick={() => scrollToSection('features')}>Features</button></li>
                <li><button onClick={() => scrollToSection('demos')}>Overview</button></li>
                <li><button onClick={() => scrollToSection('pricing')}>Membership</button></li>
              </ul>
            </div>
            <div className="footer-links">
              <h4>Company</h4>
              <ul>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div className="footer-links">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/termsofservice">Terms of Service</Link></li>
                <li><Link to="/privacypolicy">Privacy Policy</Link></li>
                <li><Link to="/privacy-choices">Privacy Choices</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Non-Target LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;